/**
 * Portaled hover tooltip anchored to a DOM element via `parentRef`.
 *
 * Positions with `position: fixed` on `document.body`. When `wrapText` is true,
 * width is set to `maxWidth` explicitly — `maxWidth` alone does not shrink
 * fixed + width:auto content, so long messages would otherwise overflow.
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type TooltipPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

const VERTICAL_FLIPPED_PLACEMENTS: Partial<
  Record<TooltipPlacement, TooltipPlacement>
> = {
  bottom: "top",
  "bottom-left": "top-left",
  "bottom-right": "top-right",
  top: "bottom",
  "top-left": "bottom-left",
  "top-right": "bottom-right",
};

/**
 * Flip a vertical tooltip placement when it would overflow the viewport.
 *
 * @param anchorRect - Bounding box of the hover target
 * @param tooltipHeight - Rendered tooltip height in pixels
 * @param placement - Preferred tooltip placement
 * @param viewportHeight - Current viewport height in pixels
 * @param offsetPx - Gap between the anchor and tooltip
 * @returns Preferred placement or its vertical opposite when space is limited
 */
export function resolveViewportPlacement(
  anchorRect: DOMRect,
  tooltipHeight: number,
  placement: TooltipPlacement,
  viewportHeight: number,
  offsetPx: number,
): TooltipPlacement {
  const isBottomPlacement = placement.startsWith("bottom");
  const isTopPlacement = placement.startsWith("top");
  const overflowsBottom =
    anchorRect.bottom + offsetPx + tooltipHeight > viewportHeight;
  const overflowsTop = anchorRect.top - offsetPx - tooltipHeight < 0;

  if (
    (isBottomPlacement && overflowsBottom) ||
    (isTopPlacement && overflowsTop)
  ) {
    return VERTICAL_FLIPPED_PLACEMENTS[placement] ?? placement;
  }
  return placement;
}

/**
 * Computes fixed-position CSS for a tooltip relative to an anchor rect.
 *
 * @param anchorRect - Bounding box of the hover target
 * @param placement - Preferred side/corner relative to the anchor
 * @param offsetPx - Gap between anchor and tooltip edge
 * @param maxWidth - CSS max-width applied to the positioned style
 * @returns Inline styles for the portaled tooltip container
 */
export function computeTooltipPosition(
  anchorRect: DOMRect,
  placement: TooltipPlacement,
  offsetPx: number = 15,
  maxWidth: string = "500px",
): React.CSSProperties {
  const halfWidth = anchorRect.width / 2;
  const halfHeight = anchorRect.height / 2;

  switch (placement) {
    case "right":
      return {
        position: "fixed",
        top: anchorRect.top + halfHeight,
        left: anchorRect.right + offsetPx,
        transform: "translate(0, -10px)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "left":
      return {
        position: "fixed",
        top: anchorRect.top + halfHeight,
        left: anchorRect.left - offsetPx,
        transform: "translate(-100%, -10px)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "top":
      return {
        position: "fixed",
        top: anchorRect.top - offsetPx,
        left: anchorRect.left + halfWidth,
        transform: "translate(-10px, -100%)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "top-left":
      return {
        position: "fixed",
        top: anchorRect.top - offsetPx,
        left: anchorRect.left,
        transform: "translate(0, -100%)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "top-right":
      return {
        position: "fixed",
        top: anchorRect.top - offsetPx,
        left: anchorRect.right,
        transform: "translate(-100%, -100%)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "bottom-left":
      return {
        position: "fixed",
        top: anchorRect.bottom + offsetPx,
        left: anchorRect.left,
        transform: "translate(0, 0)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "bottom-right":
      return {
        position: "fixed",
        top: anchorRect.bottom + offsetPx,
        left: anchorRect.right,
        transform: "translate(-100%, 0)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "bottom":
    default:
      return {
        position: "fixed",
        top: anchorRect.bottom + offsetPx,
        left: anchorRect.left + halfWidth,
        transform: "translate(-10px, 0)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
  }
}

/**
 * Renders a hover tooltip in a portal, positioned from `parentRef`.
 *
 * @param children - Tooltip body content
 * @param title - Fallback text when `children` is absent
 * @param placement - Side/corner relative to the anchor
 * @param showTooltip - When false, renders nothing
 * @param parentRef - Anchor element used for positioning
 * @param offsetPx - Gap between anchor and tooltip (default 5)
 * @param wrapText - When true, forces width to `maxWidth` and wraps text
 * @param maxWidth - Width cap (and explicit width when wrapping); default `500px`
 * @returns Portaled tooltip node that flips vertically to remain on screen
 */
const ToolTipUsingPortal = ({
  children,
  title,
  placement,
  showTooltip,
  parentRef,
  offsetPx = 5,
  wrapText = false,
  maxWidth = "500px",
}: {
  children: React.ReactNode;
  title?: string;
  placement: TooltipPlacement;
  showTooltip: boolean;
  parentRef: React.RefObject<HTMLDivElement | null>;
  offsetPx?: number;
  wrapText?: boolean;
  maxWidth?: string;
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [resolvedPlacement, setResolvedPlacement] =
    useState<TooltipPlacement>(placement);

  useLayoutEffect(() => {
    if (!showTooltip) {
      setResolvedPlacement(placement);
      return;
    }

    const anchor = parentRef.current;
    const tooltip = tooltipRef.current;
    if (!anchor || !tooltip) return;

    const updatePlacement = () => {
      setResolvedPlacement(
        resolveViewportPlacement(
          anchor.getBoundingClientRect(),
          tooltip.getBoundingClientRect().height,
          placement,
          window.innerHeight,
          offsetPx,
        ),
      );
    };

    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    return () => window.removeEventListener("resize", updatePlacement);
  }, [offsetPx, parentRef, placement, showTooltip]);

  if (!showTooltip) return null;
  const anchor = parentRef.current;
  if (!anchor) return null;

  const rect = anchor.getBoundingClientRect();
  const style: React.CSSProperties = {
    ...computeTooltipPosition(rect, resolvedPlacement, offsetPx, maxWidth),
    boxSizing: "border-box",
    ...(wrapText
      ? {
          // Explicit width is required: maxWidth alone does not shrink
          // position:fixed + width:auto shrink-to-fit content.
          width: maxWidth,
          maxWidth,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }
      : {
          width: "fit-content",
          maxWidth,
          whiteSpace: "nowrap",
        }),
  };

  return createPortal(
    <div
      ref={tooltipRef}
      style={style}
      data-placement={resolvedPlacement}
      role="tooltip"
      className="rounded-md border border-primaryGray-16 bg-white p-2 text-tiny shadow-lg"
    >
      {children ?? title}
    </div>,
    document.body,
  );
};

export default ToolTipUsingPortal;
